"use client";

import {
  type FormEvent,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import Image from "next/image";
import { useTheme } from "next-themes";
import {
  BarChart3,
  Camera,
  Check,
  ChevronDown,
  CreditCard,
  FileText,
  HardDrive,
  LogOut,
  Monitor,
  PenTool,
  RefreshCw,
  Settings,
  ShieldCheck,
  Smartphone,
  Tablet,
  UserRound,
  X,
} from "lucide-react";
import { LANGUAGE_OPTIONS, useLanguage } from "@/components/LanguageProvider";
import { useAccentColor } from "@/components/AccentColorProvider";
import { StorageDashboard } from "@/components/settings/StorageDashboard";
import { PlanDashboard } from "@/components/settings/PlanDashboard";
import { PlanCatalog } from "@/components/settings/PlanCatalog";
import { UsageDashboard } from "@/components/settings/UsageDashboard";
import {
  APP_ACCENT_COLORS,
  APP_ACCENT_SWATCHES,
  type AppAccentColor,
} from "@/lib/appPreferences";
import {
  normalizeUserProfile,
  unwrapApiData,
  type NormalizedUserProfile,
} from "@/lib/apiResponse";
import { fetchWithAuth } from "@/lib/fetchWithAuth";
import {
  SETTINGS_LOCATION_EVENT,
  canonicalizeSettingsHash,
  closeSettings,
  ensureSettingsHistoryState,
  isPlanCatalogSettingsHash,
  openPlanCatalog,
  openSettings,
  parseSettingsHash,
  returnToPlanSettings,
  settingsHashFor,
  type SettingsTab,
} from "@/lib/settingsHash";

const API_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000";

type Feedback = {
  text: string;
  type: "success" | "error";
};

type InstructionResponse = {
  content?: string;
};

function errorText(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

export function SettingsModal() {
  const [activeTab, setActiveTab] = useState<SettingsTab | null>(null);
  const [showPlanCatalog, setShowPlanCatalog] = useState(false);

  useEffect(() => {
    const syncLocation = () => {
      const tab = parseSettingsHash(window.location.hash);
      if (tab !== null) {
        ensureSettingsHistoryState();
        if (window.location.hash !== settingsHashFor(tab)) {
          canonicalizeSettingsHash(tab);
        }
      }
      setActiveTab(tab);
      setShowPlanCatalog(isPlanCatalogSettingsHash(window.location.hash));
    };

    syncLocation();
    window.addEventListener("popstate", syncLocation);
    window.addEventListener("hashchange", syncLocation);
    window.addEventListener(SETTINGS_LOCATION_EVENT, syncLocation);
    return () => {
      window.removeEventListener("popstate", syncLocation);
      window.removeEventListener("hashchange", syncLocation);
      window.removeEventListener(SETTINGS_LOCATION_EVENT, syncLocation);
    };
  }, []);

  if (activeTab === null || typeof document === "undefined") return null;

  return createPortal(
    <SettingsDialog
      activeTab={activeTab}
      showPlanCatalog={showPlanCatalog}
    />,
    document.body,
  );
}

function SettingsDialog({
  activeTab,
  showPlanCatalog,
}: {
  activeTab: SettingsTab;
  showPlanCatalog: boolean;
}) {
  const { t } = useLanguage();
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const [userProfile, setUserProfile] = useState<NormalizedUserProfile | null>(
    null,
  );
  const [profileLoadError, setProfileLoadError] = useState(false);
  const [instructionLoadError, setInstructionLoadError] = useState(false);
  const [loadingAccount, setLoadingAccount] = useState(true);
  const [username, setUsername] = useState("");
  const [instruction, setInstruction] = useState("");
  const [originalInstruction, setOriginalInstruction] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function fetchProfile(): Promise<NormalizedUserProfile> {
      const response = await fetchWithAuth(`${API_URL}/api/users/me`);
      if (!response.ok)
        throw new Error(`Profile request failed: ${response.status}`);
      const profile = normalizeUserProfile(await response.json());
      if (!profile) throw new Error("Profile response is invalid");
      return profile;
    }

    async function fetchInstruction(): Promise<string> {
      const response = await fetchWithAuth(`${API_URL}/api/instructions/me`);
      if (!response.ok) {
        throw new Error(`Instruction request failed: ${response.status}`);
      }
      const data = unwrapApiData<InstructionResponse>(await response.json());
      return data?.content ?? "";
    }

    async function loadAccountSettings() {
      const [profileResult, instructionResult] = await Promise.allSettled([
        fetchProfile(),
        fetchInstruction(),
      ]);
      if (cancelled) return;

      if (profileResult.status === "fulfilled") {
        setUserProfile(profileResult.value);
        setUsername(profileResult.value.username);
      } else {
        setProfileLoadError(true);
      }

      if (instructionResult.status === "fulfilled") {
        setInstruction(instructionResult.value);
        setOriginalInstruction(instructionResult.value);
      } else {
        setInstructionLoadError(true);
      }

      setLoadingAccount(false);
    }

    void loadAccountSettings();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const previousActiveElement =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const focusFrame = window.requestAnimationFrame(() =>
      closeButtonRef.current?.focus(),
    );
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeSettings();
        return;
      }
      if (event.key !== "Tab" || !dialogRef.current) return;

      const focusable = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(
          'a[href], button:not(:disabled), input:not(:disabled), textarea:not(:disabled), select:not(:disabled), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter(
        (element) =>
          !element.hasAttribute("hidden") &&
          element.getClientRects().length > 0 &&
          window.getComputedStyle(element).visibility !== "hidden",
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
      previousActiveElement?.focus();
    };
  }, []);

  const tabs = useMemo(
    () => [
      { id: "general" as const, label: t("settings.general"), Icon: Settings },
      { id: "profile" as const, label: t("settings.profile"), Icon: UserRound },
      {
        id: "instructions" as const,
        label: t("settings.instructions"),
        Icon: FileText,
      },
      { id: "plan" as const, label: t("settings.plan"), Icon: CreditCard },
      { id: "usage" as const, label: t("settings.usage"), Icon: BarChart3 },
      {
        id: "storage" as const,
        label: t("settings.storage"),
        Icon: HardDrive,
      },
      {
        id: "security" as const,
        label: t("settings.account_security"),
        Icon: ShieldCheck,
      },
      {
        id: "devices" as const,
        label: t("settings.devices"),
        Icon: Monitor,
      },
    ],
    [t],
  );

  const isPlanCatalogOpen = activeTab === "plan" && showPlanCatalog;

  return (
    <div
      className="fixed inset-0 z-[11000] flex items-center justify-center bg-transparent p-0 backdrop-blur-none animate-in fade-in duration-150 md:bg-black/[0.07] md:p-5 md:backdrop-blur-[2px] md:dark:bg-black/30"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) closeSettings();
      }}
    >
      <style>{`
        .settings-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: #d1d5db transparent;
        }
        .settings-scrollbar::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }
        .settings-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .settings-scrollbar::-webkit-scrollbar-thumb {
          background-color: #d1d5db;
          border-radius: 8px;
        }
        .settings-scrollbar:hover::-webkit-scrollbar-thumb {
          background-color: #9ca3af;
        }
        .dark .settings-scrollbar {
          scrollbar-color: #4b5563 transparent;
        }
        .dark .settings-scrollbar::-webkit-scrollbar-thumb {
          background-color: #4b5563;
        }
        .dark .settings-scrollbar:hover::-webkit-scrollbar-thumb {
          background-color: #6b7280;
        }
      `}</style>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="settings-dialog-title"
        className="relative flex h-dvh w-screen max-w-none flex-col overflow-hidden rounded-none border-0 bg-white shadow-none animate-in fade-in duration-150 dark:bg-[#171719] md:h-[min(580px,calc(100dvh-40px))] md:w-full md:max-w-[660px] md:flex-row md:rounded-[20px] md:border md:border-black/10 md:shadow-[0_18px_55px_rgba(0,0,0,0.18)] md:zoom-in-95 md:dark:border-white/10"
      >
        <h1 id="settings-dialog-title" className="sr-only">
          {t("settings.title")}
        </h1>
        <button
          ref={closeButtonRef}
          type="button"
          onClick={closeSettings}
          aria-label={t("settings.close") || "설정 닫기"}
          className={`absolute left-3 top-[calc(12px+env(safe-area-inset-top))] z-20 flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-black/5 hover:text-gray-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-seexr-primary/30 dark:text-gray-400 dark:hover:bg-white/10 dark:hover:text-gray-100 md:top-3 ${isPlanCatalogOpen ? "md:left-auto md:right-3" : ""}`}
        >
          <X size={18} strokeWidth={1.8} />
        </button>

        <header className="flex h-[calc(54px+env(safe-area-inset-top))] shrink-0 items-center border-b border-gray-200 pl-14 pr-4 pt-[env(safe-area-inset-top)] dark:border-[#2d2d30] md:hidden">
          <span className="text-[14px] font-semibold text-gray-950 dark:text-gray-50">
            {t("settings.title")}
          </span>
        </header>

        <nav
          aria-label={t("settings.title")}
          className="scrollbar-hide flex shrink-0 gap-1 overflow-x-auto border-b border-gray-200 bg-[#f7f7f6] px-2 py-2 dark:border-[#2d2d30] dark:bg-[#111113] md:hidden"
        >
          {tabs.map((tab) => (
            <SettingsTabButton
              key={tab.id}
              active={activeTab === tab.id}
              compact
              Icon={tab.Icon}
              label={tab.label}
              onClick={() => {
                openSettings(tab.id);
              }}
            />
          ))}
        </nav>

        {!isPlanCatalogOpen && (
          <aside className="hidden w-[184px] shrink-0 border-r border-gray-200 bg-[#f7f7f6] px-2 pb-3 pt-14 dark:border-[#2d2d30] dark:bg-[#111113] md:block">
            <nav
              aria-label={t("settings.title")}
              className="flex flex-col gap-0.5"
            >
              {tabs.map((tab) => (
                <SettingsTabButton
                  key={tab.id}
                  active={activeTab === tab.id}
                  Icon={tab.Icon}
                  label={tab.label}
                  onClick={() => {
                    openSettings(tab.id);
                  }}
                />
              ))}
            </nav>
          </aside>
        )}

        <main className="settings-scrollbar min-h-0 min-w-0 flex-1 overflow-y-auto bg-white dark:bg-[#171719]">
          <div className="w-full px-4 py-5 pb-[max(20px,env(safe-area-inset-bottom))] md:px-6 md:py-6">
            {activeTab === "general" && <GeneralSettings />}
            {activeTab === "profile" && (
              <ProfileSettings
                loading={loadingAccount}
                loadError={profileLoadError}
                userProfile={userProfile}
                username={username}
                setUsername={setUsername}
                setUserProfile={setUserProfile}
              />
            )}
            {activeTab === "instructions" && (
              <InstructionSettings
                loading={loadingAccount}
                loadError={instructionLoadError}
                instruction={instruction}
                originalInstruction={originalInstruction}
                setInstruction={setInstruction}
                setOriginalInstruction={setOriginalInstruction}
              />
            )}
            {activeTab === "plan" &&
              (isPlanCatalogOpen ? (
                <PlanCatalog
                  embedded
                  onBack={returnToPlanSettings}
                />
              ) : (
                <PlanSettings onViewPlans={openPlanCatalog} />
              ))}
            {activeTab === "usage" && <UsageDashboard />}
            {activeTab === "storage" && <StorageDashboard />}
            {activeTab === "security" && <SecuritySettings />}
            {activeTab === "devices" && <DevicesSettings />}
          </div>
        </main>
      </div>
    </div>
  );
}

function SettingsTabButton({
  active,
  compact = false,
  Icon,
  label,
  onClick,
}: {
  active: boolean;
  compact?: boolean;
  Icon: typeof Settings;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      className={`flex shrink-0 items-center gap-2.5 rounded-lg text-[12.5px] font-medium transition-colors ${
        compact ? "px-3 py-2" : "w-full px-3 py-2 text-left"
      } ${
        active
          ? "bg-black/[0.055] text-gray-950 dark:bg-white/10 dark:text-gray-50"
          : "text-gray-600 hover:bg-black/[0.035] hover:text-gray-950 dark:text-gray-400 dark:hover:bg-white/[0.06] dark:hover:text-gray-100"
      }`}
    >
      <Icon size={16} strokeWidth={1.8} className="shrink-0" />
      <span className="whitespace-nowrap">{label}</span>
    </button>
  );
}

function ContentHeading({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <div className="mb-4 border-b border-gray-200 pb-4 dark:border-[#2d2d30]">
      <h2 className="text-[16px] font-semibold tracking-tight text-gray-950 dark:text-gray-50">
        {title}
      </h2>
      <p className="mt-1 text-[11.5px] leading-relaxed text-gray-500 dark:text-gray-400">
        {description}
      </p>
    </div>
  );
}

function SettingsSection({
  title,
  description,
  action,
  children,
  showBottomDivider = true,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  children: ReactNode;
  showBottomDivider?: boolean;
}) {
  return (
    <section
      className={
        showBottomDivider
          ? "border-b border-gray-200 dark:border-[#2d2d30]"
          : undefined
      }
    >
      <div className="flex items-center justify-between gap-4 py-3.5">
        <div className="min-w-0">
          <h3 className="text-[13px] font-semibold text-gray-900 dark:text-gray-100">
            {title}
          </h3>
          {description && (
            <p className="mt-0.5 text-[11px] leading-relaxed text-gray-500 dark:text-gray-400">
              {description}
            </p>
          )}
        </div>
        {action && <div className="shrink-0">{action}</div>}
      </div>
      <div className="border-t border-gray-100 py-4 dark:border-[#29292c]">
        {children}
      </div>
    </section>
  );
}

function GeneralSettings() {
  const { t, language, setLanguage } = useLanguage();
  const { theme, setTheme } = useTheme();
  const themeOptions = [
    { value: "system", label: t("footer.system") },
    { value: "light", label: t("footer.light") },
    { value: "dark", label: t("footer.dark") },
  ];

  return (
    <>
      <ContentHeading
        title={t("settings.general")}
        description={t("settings.general_desc")}
      />
      <div className="divide-y divide-gray-200 border-b border-gray-200 dark:divide-[#2d2d30] dark:border-[#2d2d30]">
        <CompactSelectRow
          label={t("settings.appearance")}
          description={t("settings.appearance_desc")}
          value={theme || "system"}
          options={themeOptions}
          onChange={setTheme}
        />
        <AccentColorRow />
        <CompactSelectRow
          label={t("footer.language")}
          description={t("settings.language_desc")}
          value={language}
          options={LANGUAGE_OPTIONS}
          onChange={(value) => setLanguage(value as typeof language)}
        />
      </div>
    </>
  );
}

function AccentColorRow() {
  const { t } = useLanguage();
  const { accentColor, status, saving, saveError, reload, saveAccentColor } =
    useAccentColor();
  const accentOptions = APP_ACCENT_COLORS.map((color) => ({
    value: color,
    label: t(`settings.accent_color.${color}`),
    swatchColor: APP_ACCENT_SWATCHES[color],
  }));

  if (status === "error") {
    return (
      <div className="flex min-h-[68px] items-center justify-between gap-5 py-3">
        <div className="min-w-0">
          <p className="text-[12.5px] font-medium text-gray-900 dark:text-gray-100">
            {t("settings.accent_color")}
          </p>
          <p className="mt-0.5 line-clamp-2 text-[10.5px] leading-relaxed text-gray-500 dark:text-gray-400">
            {t("settings.accent_color_desc")}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <span className="text-[10.5px] text-gray-500 dark:text-gray-400">
            {t("settings.accent_color_load_error")}
          </span>
          <button
            type="button"
            onClick={() => void reload()}
            className="h-8 rounded-lg px-2.5 text-[11.5px] font-semibold text-gray-600 transition-colors hover:bg-black/[0.04] hover:text-gray-950 dark:text-gray-300 dark:hover:bg-white/[0.07] dark:hover:text-white"
          >
            {t("settings.retry")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <CompactSelectRow
      label={t("settings.accent_color")}
      description={t("settings.accent_color_desc")}
      value={accentColor}
      options={accentOptions}
      onChange={(value) => void saveAccentColor(value as AppAccentColor)}
      disabled={status === "loading" || saving}
      busy={status === "loading" || saving}
      feedback={
        <>
          {status === "loading" && (
            <span role="status" className="sr-only">
              {t("settings.accent_color_loading")}
            </span>
          )}
          {saveError && (
            <span
              role="alert"
              className="mt-1 block text-[10.5px] font-medium text-red-600 dark:text-red-400"
            >
              {t("settings.accent_color_save_error")}
            </span>
          )}
        </>
      }
    />
  );
}

function CompactSelectRow({
  label,
  description,
  value,
  options,
  onChange,
  disabled = false,
  busy = false,
  feedback,
}: {
  label: string;
  description: string;
  value: string;
  options: readonly {
    value: string;
    label: string;
    swatchColor?: string;
  }[];
  onChange: (value: string) => void;
  disabled?: boolean;
  busy?: boolean;
  feedback?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const selectedOption = options.find((option) => option.value === value);
  const selectedLabel = selectedOption?.label ?? value;

  useEffect(() => {
    if (!open) return;

    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
    };

    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape, true);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape, true);
    };
  }, [open]);

  return (
    <div className="flex min-h-[68px] items-center justify-between gap-5 py-3">
      <div className="min-w-0">
        <p className="text-[12.5px] font-medium text-gray-900 dark:text-gray-100">
          {label}
        </p>
        <p className="mt-0.5 line-clamp-2 text-[10.5px] leading-relaxed text-gray-500 dark:text-gray-400">
          {description}
        </p>
        {feedback}
      </div>
      <div ref={rootRef} className="relative shrink-0">
        <button
          type="button"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-busy={busy}
          disabled={disabled}
          onClick={() => setOpen((current) => !current)}
          className={`flex h-8 min-w-[96px] max-w-[150px] items-center justify-end gap-1.5 rounded-lg px-2 text-[12px] font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-seexr-primary/25 disabled:cursor-wait disabled:opacity-55 ${
            open
              ? "bg-black/[0.055] text-gray-950 dark:bg-white/10 dark:text-gray-100"
              : "text-gray-700 hover:bg-black/[0.035] dark:text-gray-300 dark:hover:bg-white/[0.06]"
          }`}
        >
          {selectedOption?.swatchColor && (
            <span
              aria-hidden="true"
              className="h-3 w-3 shrink-0 rounded-full ring-1 ring-black/10 dark:ring-white/15"
              style={{ backgroundColor: selectedOption.swatchColor }}
            />
          )}
          <span className="truncate">{selectedLabel}</span>
          <ChevronDown
            size={13}
            strokeWidth={1.8}
            className={`shrink-0 text-gray-500 transition-transform duration-150 ${
              open ? "rotate-180" : ""
            }`}
          />
        </button>
        {open && (
          <div
            role="listbox"
            aria-label={label}
            className="absolute right-0 top-[calc(100%+5px)] z-[80] max-h-64 min-w-[156px] overflow-y-auto rounded-xl border border-gray-200 bg-white p-1 shadow-[0_10px_30px_rgba(15,23,42,0.12)] animate-in fade-in zoom-in-95 duration-100 dark:border-[#343439] dark:bg-[#232326]"
          >
            {options.map((option) => {
              const selected = option.value === value;
              return (
                <button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  onClick={() => {
                    onChange(option.value);
                    setOpen(false);
                  }}
                  className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-[11.5px] font-medium transition-colors ${
                    selected
                      ? "bg-seexr-solid/10 text-seexr-primary"
                      : "text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-[#303034]"
                  }`}
                >
                  {option.swatchColor && (
                    <span
                      aria-hidden="true"
                      className="h-3.5 w-3.5 shrink-0 rounded-full ring-1 ring-black/10 dark:ring-white/15"
                      style={{ backgroundColor: option.swatchColor }}
                    />
                  )}
                  <span className="min-w-0 flex-1 truncate">
                    {option.label}
                  </span>
                  {selected && <Check size={13} className="shrink-0" />}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function ProfileSettings({
  loading,
  loadError,
  userProfile,
  username,
  setUsername,
  setUserProfile,
}: {
  loading: boolean;
  loadError: boolean;
  userProfile: NormalizedUserProfile | null;
  username: string;
  setUsername: (value: string) => void;
  setUserProfile: (profile: NormalizedUserProfile | null) => void;
}) {
  const { t } = useLanguage();
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<Feedback | null>(null);

  async function handleSave(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setFeedback(null);
    try {
      const response = await fetchWithAuth(`${API_URL}/api/users/me`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fullName: username.trim() }),
      });
      if (!response.ok) throw new Error(t("settings.profile_error"));

      const profile = normalizeUserProfile(await response.json());
      if (!profile) throw new Error(t("settings.profile_error"));
      setUserProfile(profile);
      setUsername(profile.username);
      setFeedback({ text: t("settings.profile_success"), type: "success" });
    } catch (error) {
      setFeedback({
        text: errorText(error, t("settings.profile_error")),
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <ContentHeading
        title={t("settings.profile")}
        description={t("settings.profile_desc")}
      />
      {loading ? (
        <SettingsSkeleton />
      ) : loadError || !userProfile ? (
        <InlineLoadError />
      ) : (
        <div className="flex flex-col gap-5">
          <SettingsSection
            title={t("settings.profile_info")}
            description={t("settings.profile_desc")}
          >
            <form onSubmit={handleSave} className="flex flex-col gap-4">
              <Field
                label={t("settings.email_readonly")}
                description={t("settings.email_desc")}
              >
                <input
                  disabled
                  type="email"
                  value={userProfile.email}
                  className="h-9 w-full rounded-lg border border-gray-200 bg-gray-50 px-3 text-[12px] font-medium text-gray-400 outline-none dark:border-[#303034] dark:bg-black/15 dark:text-gray-500"
                />
              </Field>
              <Field
                label={t("settings.username")}
                description={t("settings.username_desc")}
              >
                <input
                  type="text"
                  value={username}
                  onChange={(event) => setUsername(event.target.value)}
                  placeholder={t("settings.username_placeholder")}
                  className="h-9 w-full rounded-lg border border-gray-200 bg-white px-3 text-[12px] font-medium text-gray-900 outline-none transition-all focus:border-gray-400 focus:ring-2 focus:ring-black/5 dark:border-[#343439] dark:bg-[#171719] dark:text-gray-100 dark:focus:border-gray-500 dark:focus:ring-white/5"
                />
              </Field>
              {feedback && <FeedbackMessage feedback={feedback} />}
              <button
                type="submit"
                disabled={
                  saving ||
                  !username.trim() ||
                  username.trim() === userProfile.username
                }
                className="inline-flex h-9 w-fit items-center rounded-lg bg-gray-950 px-4 text-[12px] font-semibold text-white transition-colors hover:bg-black disabled:opacity-40 dark:bg-white dark:text-gray-950 dark:hover:bg-gray-200"
              >
                {saving ? t("settings.saving") : t("settings.save_changes")}
              </button>
            </form>
          </SettingsSection>

          <SettingsSection
            title={t("settings.profile_media")}
            description={t("settings.profile_media_pending_desc")}
          >
            <div className="divide-y divide-gray-100 dark:divide-[#2a2a2e]">
              <ReadOnlyMediaRow
                title={t("settings.profile_photo")}
                fileId={userProfile.avatar_file_id}
                Icon={Camera}
                circle
              />
              <ReadOnlyMediaRow
                title={t("settings.signature")}
                fileId={userProfile.signature_file_id}
                Icon={PenTool}
              />
            </div>
          </SettingsSection>
        </div>
      )}
    </>
  );
}

function InstructionSettings({
  loading,
  loadError,
  instruction,
  originalInstruction,
  setInstruction,
  setOriginalInstruction,
}: {
  loading: boolean;
  loadError: boolean;
  instruction: string;
  originalInstruction: string;
  setInstruction: (value: string) => void;
  setOriginalInstruction: (value: string) => void;
}) {
  const { t } = useLanguage();
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<Feedback | null>(null);

  async function handleSave(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setFeedback(null);
    try {
      const response = await fetchWithAuth(`${API_URL}/api/instructions/me`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content: instruction }),
      });
      if (!response.ok) throw new Error(t("settings.instructions_error"));

      const data = unwrapApiData<InstructionResponse>(await response.json());
      const content = data?.content ?? "";
      setInstruction(content);
      setOriginalInstruction(content);
      setFeedback({
        text: t("settings.instructions_success"),
        type: "success",
      });
    } catch (error) {
      setFeedback({
        text: errorText(error, t("settings.instructions_error")),
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <ContentHeading
        title={t("settings.instructions")}
        description={t("settings.instructions_desc")}
      />
      {loading ? (
        <SettingsSkeleton />
      ) : loadError ? (
        <InlineLoadError />
      ) : (
        <SettingsSection
          title={t("settings.instructions_title")}
          description={t("settings.instructions_hint")}
          showBottomDivider={false}
        >
          <form onSubmit={handleSave} className="flex flex-col gap-3">
            <textarea
              value={instruction}
              onChange={(event) => setInstruction(event.target.value)}
              rows={9}
              placeholder={t("settings.instructions_placeholder")}
              className="min-h-[190px] w-full resize-y rounded-[10px] border border-gray-200 bg-white px-3 py-2.5 text-[12px] font-medium leading-relaxed text-gray-900 outline-none transition-all focus:border-gray-400 focus:ring-2 focus:ring-black/5 dark:border-[#343439] dark:bg-[#171719] dark:text-gray-100 dark:focus:border-gray-500 dark:focus:ring-white/5"
            />
            {feedback && <FeedbackMessage feedback={feedback} />}
            <button
              type="submit"
              disabled={saving || instruction === originalInstruction}
              className="inline-flex h-9 w-fit items-center rounded-lg bg-gray-950 px-4 text-[12px] font-semibold text-white transition-colors hover:bg-black disabled:opacity-40 dark:bg-white dark:text-gray-950 dark:hover:bg-gray-200"
            >
              {saving ? t("settings.saving") : t("settings.save_changes")}
            </button>
          </form>
        </SettingsSection>
      )}
    </>
  );
}

function PlanSettings({ onViewPlans }: { onViewPlans: () => void }) {
  const { t } = useLanguage();
  return (
    <>
      <ContentHeading
        title={t("settings.plan")}
        description={t("settings.plan_desc")}
      />
      <PlanDashboard onViewPlans={onViewPlans} />
    </>
  );
}

function SecuritySettings() {
  const { t } = useLanguage();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<Feedback | null>(null);

  async function handleSave(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setFeedback(null);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/api/auth/change-password`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ currentPassword, newPassword }),
        },
      );
      const payload: unknown = await response.json().catch(() => undefined);
      if (!response.ok) {
        const data = unwrapApiData<{
          message?: string;
          error?: { message?: string };
        }>(payload);
        throw new Error(
          data?.error?.message || data?.message || t("settings.pwd_error"),
        );
      }

      setCurrentPassword("");
      setNewPassword("");
      setFeedback({ text: t("settings.pwd_success"), type: "success" });
    } catch (error) {
      setFeedback({
        text: errorText(error, t("settings.pwd_error")),
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <ContentHeading
        title={t("settings.account_security")}
        description={t("settings.security_desc")}
      />
      <SettingsSection
        title={t("settings.change_pwd")}
        description={t("settings.security_desc")}
        showBottomDivider={false}
      >
        <form onSubmit={handleSave} className="flex max-w-xl flex-col gap-4">
          <Field label={t("settings.current_password")}>
            <input
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
              placeholder={t("settings.current_password_placeholder")}
              className="h-9 w-full rounded-lg border border-gray-200 bg-white px-3 text-[12px] font-medium text-gray-900 outline-none transition-all focus:border-gray-400 focus:ring-2 focus:ring-black/5 dark:border-[#343439] dark:bg-[#171719] dark:text-gray-100 dark:focus:border-gray-500 dark:focus:ring-white/5"
            />
          </Field>
          <Field label={t("settings.new_password")}>
            <input
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              placeholder={t("settings.new_password_placeholder")}
              className="h-9 w-full rounded-lg border border-gray-200 bg-white px-3 text-[12px] font-medium text-gray-900 outline-none transition-all focus:border-gray-400 focus:ring-2 focus:ring-black/5 dark:border-[#343439] dark:bg-[#171719] dark:text-gray-100 dark:focus:border-gray-500 dark:focus:ring-white/5"
            />
          </Field>
          {feedback && <FeedbackMessage feedback={feedback} />}
          <button
            type="submit"
            disabled={saving || !currentPassword.trim() || !newPassword.trim()}
            className="inline-flex h-9 w-fit items-center rounded-lg bg-gray-950 px-4 text-[12px] font-semibold text-white transition-colors hover:bg-black disabled:opacity-40 dark:bg-white dark:text-gray-950 dark:hover:bg-gray-200"
          >
            {saving ? t("settings.updating") : t("settings.change_pwd")}
          </button>
        </form>
      </SettingsSection>
    </>
  );
}

type LoginDevice = {
  deviceSessionId: string;
  deviceName: string;
  deviceType: "desktop" | "mobile" | "tablet" | string;
  browser: string;
  os: string;
  ipAddress: string | null;
  signedInAt: string;
  lastSeenAt: string;
  expiresAt: string;
  isCurrent: boolean;
};

type LoginDevicesResponse = {
  devices: LoginDevice[];
  activeCount: number;
  maxDevices: number | null;
};

function DevicesSettings() {
  const { t } = useLanguage();
  const [data, setData] = useState<LoginDevicesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<Feedback | null>(null);

  async function loadDevices() {
    setLoading(true);
    setFeedback(null);
    try {
      const response = await fetchWithAuth(`${API_URL}/api/auth/devices`);
      const payload: unknown = await response.json().catch(() => undefined);
      if (!response.ok) throw new Error(t("settings.devices_load_error"));
      const result = unwrapApiData<LoginDevicesResponse>(payload);
      if (!result?.devices) throw new Error(t("settings.devices_load_error"));
      setData(result);
    } catch (error) {
      setFeedback({
        type: "error",
        text: errorText(error, t("settings.devices_load_error")),
      });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadDevices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function revokeDevice(device: LoginDevice) {
    if (device.isCurrent) return;
    setRevokingId(device.deviceSessionId);
    setFeedback(null);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/api/auth/devices/${device.deviceSessionId}`,
        { method: "DELETE" },
      );
      if (!response.ok) throw new Error(t("settings.devices_revoke_error"));
      await loadDevices();
      setFeedback({
        type: "success",
        text: t("settings.devices_revoke_success"),
      });
    } catch (error) {
      setFeedback({
        type: "error",
        text: errorText(error, t("settings.devices_revoke_error")),
      });
    } finally {
      setRevokingId(null);
    }
  }

  return (
    <>
      <ContentHeading
        title={t("settings.devices_title")}
        description={t("settings.devices_desc")}
      />
      <SettingsSection
        title={t("settings.devices_active")}
        description={
          data
            ? `${data.activeCount} / ${
                data.maxDevices === null
                  ? t("settings.devices_unlimited")
                  : `${data.maxDevices}`
              }`
            : t("settings.devices_desc")
        }
        action={
          <button
            type="button"
            onClick={() => void loadDevices()}
            disabled={loading}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-[11px] font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-40 dark:border-[#343439] dark:text-gray-300 dark:hover:bg-white/5"
          >
            <RefreshCw
              className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`}
            />
            {t("settings.devices_refresh")}
          </button>
        }
        showBottomDivider={false}
      >
        <div className="max-w-2xl">
          {feedback && <FeedbackMessage feedback={feedback} />}
          {loading && !data ? (
            <p className="py-10 text-center text-[12px] text-gray-500 dark:text-gray-400">
              {t("settings.devices_loading")}
            </p>
          ) : (
            <div className="divide-y divide-gray-200 border-b border-gray-200 dark:divide-[#2a2a2e] dark:border-[#2a2a2e]">
              {data?.devices.map((device) => (
                <DeviceRow
                  key={device.deviceSessionId}
                  device={device}
                  revoking={revokingId === device.deviceSessionId}
                  onRevoke={() => void revokeDevice(device)}
                />
              ))}
              {data?.devices.length === 0 && (
                <p className="py-10 text-center text-[12px] text-gray-500 dark:text-gray-400">
                  {t("settings.devices_empty")}
                </p>
              )}
            </div>
          )}
        </div>
      </SettingsSection>
    </>
  );
}

function DeviceRow({
  device,
  revoking,
  onRevoke,
}: {
  device: LoginDevice;
  revoking: boolean;
  onRevoke: () => void;
}) {
  const { t } = useLanguage();
  const DeviceIcon =
    device.deviceType === "mobile"
      ? Smartphone
      : device.deviceType === "tablet"
        ? Tablet
        : Monitor;
  return (
    <div className="flex items-center gap-3 py-4">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-600 dark:bg-white/5 dark:text-gray-300">
        <DeviceIcon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="truncate text-[12.5px] font-semibold text-gray-950 dark:text-gray-50">
            {device.deviceName}
          </p>
          {device.isCurrent && (
            <span className="rounded-md bg-emerald-50 px-1.5 py-0.5 text-[9px] font-semibold text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-400">
              {t("settings.devices_current")}
            </span>
          )}
        </div>
        <p className="mt-1 text-[10.5px] text-gray-500 dark:text-gray-400">
          {t("settings.devices_last_seen")}{" "}
          {new Date(device.lastSeenAt).toLocaleString()}
          {device.ipAddress ? ` · ${device.ipAddress}` : ""}
        </p>
      </div>
      {!device.isCurrent && (
        <button
          type="button"
          onClick={onRevoke}
          disabled={revoking}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-lg px-2.5 py-2 text-[10.5px] font-semibold text-red-600 transition-colors hover:bg-red-50 disabled:opacity-40 dark:text-red-400 dark:hover:bg-red-950/20"
        >
          <LogOut className="h-3.5 w-3.5" />
          {revoking
            ? t("settings.devices_revoking")
            : t("settings.devices_revoke")}
        </button>
      )}
    </div>
  );
}

function Field({
  label,
  description,
  children,
}: {
  label: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[11.5px] font-medium text-gray-700 dark:text-gray-300">
        {label}
      </span>
      {children}
      {description && (
        <span className="text-[10.5px] leading-relaxed text-gray-500 dark:text-gray-400">
          {description}
        </span>
      )}
    </label>
  );
}

function FeedbackMessage({ feedback }: { feedback: Feedback }) {
  return (
    <div
      className={`rounded-lg border px-3 py-2 text-[11px] font-medium ${
        feedback.type === "success"
          ? "border-emerald-100 bg-emerald-50 text-emerald-700 dark:border-emerald-900/40 dark:bg-emerald-950/20 dark:text-emerald-400"
          : "border-red-100 bg-red-50 text-red-600 dark:border-red-900/40 dark:bg-red-950/20 dark:text-red-400"
      }`}
    >
      {feedback.text}
    </div>
  );
}

function SettingsSkeleton() {
  return (
    <div className="animate-pulse border-b border-gray-200 py-4 dark:border-[#303034]">
      <div className="h-3.5 w-28 rounded bg-gray-100 dark:bg-white/5" />
      <div className="mt-5 h-9 w-full rounded-lg bg-gray-100 dark:bg-white/5" />
      <div className="mt-3 h-9 w-full rounded-lg bg-gray-100 dark:bg-white/5" />
      <div className="mt-5 h-9 w-20 rounded-lg bg-gray-100 dark:bg-white/5" />
    </div>
  );
}

function InlineLoadError() {
  const { t } = useLanguage();
  return (
    <div className="border-b border-red-100 bg-red-50/60 px-4 py-6 text-center text-[11.5px] font-medium text-red-600 dark:border-red-900/30 dark:bg-red-950/15 dark:text-red-400">
      {t("settings.load_error")}
    </div>
  );
}

function ReadOnlyMediaRow({
  title,
  fileId,
  Icon,
  circle = false,
}: {
  title: string;
  fileId: string | null | undefined;
  Icon: typeof Camera;
  circle?: boolean;
}) {
  const { t } = useLanguage();
  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!fileId) return;
    let disposed = false;
    let createdUrl: string | null = null;

    fetchWithAuth(`${API_URL}/api/files/${fileId}/content`, {
      skipRedirectOn401: true,
    })
      .then((response) => (response.ok ? response.blob() : null))
      .then((blob) => {
        if (!blob || disposed) return;
        createdUrl = URL.createObjectURL(blob);
        setBlobUrl(createdUrl);
      })
      .catch(() => undefined);

    return () => {
      disposed = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [fileId]);

  return (
    <div className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
      <div
        className={`relative flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden border border-gray-200 bg-gray-50 text-gray-400 dark:border-[#3a3a3e] dark:bg-black/15 dark:text-gray-500 ${
          circle ? "rounded-full" : "rounded-lg"
        }`}
      >
        {blobUrl ? (
          <Image
            src={blobUrl}
            alt={title}
            fill
            unoptimized
            sizes="44px"
            className={circle ? "object-cover" : "object-contain p-1"}
          />
        ) : (
          <Icon size={18} strokeWidth={1.8} />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[12px] font-medium text-gray-800 dark:text-gray-200">
          {title}
        </p>
        <p className="mt-0.5 text-[10px] text-gray-500 dark:text-gray-400">
          {fileId ? t("settings.media_registered") : t("settings.media_empty")}
        </p>
      </div>
      <span className="shrink-0 rounded-md bg-gray-100 px-2 py-1 text-[9px] font-semibold text-gray-500 dark:bg-white/5 dark:text-gray-400">
        {t("settings.coming_soon")}
      </span>
    </div>
  );
}
